import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import ArrowRotateClockwiseIcon from "@src/lib/icons/18px/arrow-rotate-clockwise";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Slider } from "@src/lib/ui/Slider";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import {
  MAX_ZOOM,
  MIN_ZOOM,
  OutputFormat,
  Point,
  Rect,
  ResizeHandle,
  Rotation,
  Size,
  SourceImageType,
  View,
  applyAspect,
  clampCenter,
  clampZoom,
  defaultCrop,
  fitScale,
  imageToStage,
  moveRect,
  nextRotation,
  outputFileName,
  outputFormatsFor,
  outputSize,
  rectToStage,
  resizeRect,
  rotatePointClockwise,
  rotateRectClockwise,
  rotatedSize,
  rotationTransform,
  sniffImageType,
  webpHasAlpha,
  wheelDeltaPixels,
  zoomCenterAround,
} from "@src/lib/ui/imageCropMath";

type BaseProps = {
  imageUrl: string;
  visible: boolean;
  hide: () => void;
  /** Initial crop aspect ratio (width / height); free-form when unset. */
  aspectRatio?: number;
  /** Hide the aspect presets so the crop stays at `aspectRatio`. */
  lockAspectRatio?: boolean;
};

type WithNewImageFile = {
  onNewImageFile: (file: File) => void;
  onNewImageUrl?: never;
};

type WithNewImageUrl = {
  onNewImageFile?: never;
  /** The cropped image is uploaded to S3 and its URL passed here. */
  onNewImageUrl: (imageUrl: string) => void;
};

type Props = BaseProps & (WithNewImageFile | WithNewImageUrl);

type LoadedImage = {
  element: HTMLImageElement;
  natural: Size;
  type: SourceImageType;
  hasAlpha: boolean;
};

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; image: LoadedImage };

const ASPECT_PRESETS: { label: string; value: number | null }[] = [
  { label: "Free", value: null },
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
  { label: "3:4", value: 3 / 4 },
  { label: "9:16", value: 9 / 16 },
];

const HANDLES: { handle: ResizeHandle; className: string }[] = [
  { handle: "nw", className: "left-0 top-0 cursor-nwse-resize" },
  { handle: "n", className: "left-1/2 top-0 cursor-ns-resize" },
  { handle: "ne", className: "left-full top-0 cursor-nesw-resize" },
  { handle: "e", className: "left-full top-1/2 cursor-ew-resize" },
  { handle: "se", className: "left-full top-full cursor-nwse-resize" },
  { handle: "s", className: "left-1/2 top-full cursor-ns-resize" },
  { handle: "sw", className: "left-0 top-full cursor-nesw-resize" },
  { handle: "w", className: "left-0 top-1/2 cursor-ew-resize" },
];

/**
 * The dialog is portalled, but React still bubbles its synthetic events to
 * the React ancestors of wherever `ImageCropDialog` is rendered: a step
 * card's onClick, a sortable card's dnd-kit listeners, a builder's hotkeys.
 * Pintura mounted outside React, so call sites never had to guard against
 * that; stopping at the content root keeps them from reacting to the editor.
 * Handlers inside the dialog, and Radix's own on the content element, still
 * run: they fire before the event leaves the root.
 */
const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

/**
 * `stopPropagation` also stops the native event, but Radix's outside-press
 * detection needs every `pointerdown` to reach the document (it flags inside
 * presses in the capture phase and clears the flag there); stopping it would
 * make the next overlay click fail to close the dialog. So pointer presses
 * are stopped for React handlers only, via the flag React checks before each
 * one.
 */
const stopReactPropagation = (event: SyntheticEvent) => {
  event.isPropagationStopped = () => true;
};

const CONTAINED_EVENTS = {
  onClick: stopPropagation,
  onDoubleClick: stopPropagation,
  onContextMenu: stopPropagation,
  onMouseDown: stopPropagation,
  onMouseUp: stopPropagation,
  onPointerDown: stopReactPropagation,
  onPointerUp: stopReactPropagation,
  onKeyDown: stopPropagation,
  onKeyUp: stopPropagation,
  onWheel: stopPropagation,
} as const;

const sameAspect = (a: number | null, b: number | null): boolean =>
  a === b || (a !== null && b !== null && Math.abs(a - b) < 0.001);

/**
 * Fetches the image into an object URL so the export canvas is never
 * tainted by a cross-origin source. A CDN can hand back a cached copy that
 * an `<img>` loaded without CORS headers, so a failed fetch retries once
 * bypassing the HTTP cache.
 */
const fetchImageBlob = async (
  url: string,
  signal: AbortSignal,
): Promise<Blob> => {
  const attempt = async (cache: RequestCache) => {
    const response = await fetch(url, { signal, cache });
    if (!response.ok) {
      throw new Error(`Image request failed: ${response.status}`);
    }
    return response.blob();
  };
  try {
    return await attempt("default");
  } catch (error) {
    if (signal.aborted || url.startsWith("blob:") || url.startsWith("data:")) {
      throw error;
    }
    return attempt("reload");
  }
};

const LOADING: LoadState = { status: "loading" };

const useLoadedImage = (imageUrl: string, enabled: boolean): LoadState => {
  const [loaded, setLoaded] = useState<{ url: string; state: LoadState }>({
    url: imageUrl,
    state: LOADING,
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLoaded({ url: imageUrl, state: LOADING });
    (async () => {
      const blob = await fetchImageBlob(imageUrl, controller.signal);
      const header = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
      // Cleanup revokes `objectUrl` only if it's set by then; once aborted it
      // has already run, so a URL made now would leak.
      if (controller.signal.aborted) {
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      const element = new Image();
      element.src = objectUrl;
      await element.decode();
      if (!element.naturalWidth || !element.naturalHeight) {
        throw new Error("Image has no intrinsic size");
      }
      if (!controller.signal.aborted) {
        const type = sniffImageType(header, blob.type);
        setLoaded({
          url: imageUrl,
          state: {
            status: "ready",
            image: {
              element,
              natural: {
                width: element.naturalWidth,
                height: element.naturalHeight,
              },
              type,
              hasAlpha: type === "webp" ? webpHasAlpha(header) : true,
            },
          },
        });
      }
    })().catch((error: unknown) => {
      if (!controller.signal.aborted) {
        console.error("[ImageCropDialog] failed to load image", error);
        setLoaded({ url: imageUrl, state: { status: "error" } });
      }
    });
    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      // The object URL is gone, so a later reopen must not paint this image.
      setLoaded({ url: imageUrl, state: LOADING });
    };
  }, [imageUrl, enabled]);
  // Covers the render between a hide/URL change and the effect above
  // resetting state, so a stale or revoked image is never shown.
  return enabled && loaded.url === imageUrl ? loaded.state : LOADING;
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  format: OutputFormat,
): Promise<Blob | null> =>
  new Promise((resolve) =>
    canvas.toBlob(resolve, format.mimeType, format.quality),
  );

const renderCrop = async (
  image: LoadedImage,
  rotation: Rotation,
  crop: Rect,
  imageUrl: string,
): Promise<File> => {
  const size = outputSize(crop);
  const formats = outputFormatsFor(image.type, image.hasAlpha);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas 2D context unavailable");
  }
  if (formats.every((format) => format.mimeType !== "image/png")) {
    // Opaque output: flatten onto white rather than JPEG's black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, size.width, size.height);
  }
  context.imageSmoothingQuality = "high";
  const { translate, angle } = rotationTransform(image.natural, rotation);
  context.scale(size.scale, size.scale);
  context.translate(-crop.x, -crop.y);
  context.translate(translate.x, translate.y);
  context.rotate(angle);
  context.drawImage(
    image.element,
    0,
    0,
    image.natural.width,
    image.natural.height,
  );
  for (const [index, format] of formats.entries()) {
    const blob = await canvasToBlob(canvas, format);
    // Without an encoder for the type, toBlob quietly returns PNG instead.
    const last = index === formats.length - 1;
    if (blob && (blob.type === format.mimeType || (last && !blob.type))) {
      return new File([blob], outputFileName(imageUrl, format.extension), {
        type: format.mimeType,
      });
    }
  }
  throw new Error("Canvas export failed");
};

type Drag =
  | { mode: "move"; pointerId: number; origin: Point; startCrop: Rect }
  | {
      mode: "resize";
      pointerId: number;
      origin: Point;
      startCrop: Rect;
      handle: ResizeHandle;
    }
  | { mode: "pan"; pointerId: number; origin: Point; startCenter: Point };

type EditorProps = {
  image: LoadedImage;
  imageUrl: string;
  initialAspect: number | null;
  lockAspectRatio: boolean;
  saving: boolean;
  onCancel: () => void;
  onApply: (file: File) => void;
  onExportError: () => void;
};

const CropEditor: FC<EditorProps> = ({
  image,
  imageUrl,
  initialAspect,
  lockAspectRatio,
  saving,
  onCancel,
  onApply,
  onExportError,
}) => {
  const [rotation, setRotation] = useState<Rotation>(0);
  const [aspect, setAspect] = useState<number | null>(initialAspect);
  const [crop, setCrop] = useState<Rect>(() =>
    defaultCrop(image.natural, initialAspect),
  );
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [center, setCenter] = useState<Point>({
    x: image.natural.width / 2,
    y: image.natural.height / 2,
  });
  const [stage, setStage] = useState<Size | null>(null);
  const [exporting, setExporting] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);

  useLayoutEffect(() => {
    const element = stageRef.current;
    if (!element) {
      return;
    }
    const measure = () =>
      setStage({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const bounds = rotatedSize(image.natural, rotation);
  const view: View | null = stage
    ? { scale: fitScale(stage, bounds) * zoom, center }
    : null;

  /**
   * Zooms keeping the image point under `anchor` (a stage offset from the
   * stage centre) still, so the author's pan survives. Back at the minimum
   * zoom the whole image fits again, so the view re-centres on it.
   */
  // Several wheel events can land before a re-render, so zooming chains off
  // the latest values rather than the ones this render closed over.
  const liveView = useRef({ zoom, center });
  liveView.current = { zoom, center };
  const zoomAround = (value: number, anchor: Point) => {
    if (!stage) {
      return;
    }
    const current = liveView.current;
    const next = clampZoom(value);
    const base = fitScale(stage, bounds);
    const nextCenter =
      next === MIN_ZOOM
        ? { x: bounds.width / 2, y: bounds.height / 2 }
        : clampCenter(
            zoomCenterAround(
              current.center,
              anchor,
              base * current.zoom,
              base * next,
            ),
            bounds,
          );
    liveView.current = { zoom: next, center: nextCenter };
    setZoom(next);
    setCenter(nextCenter);
  };

  // The slider zooms around the crop's centre, keeping it where it is on
  // screen.
  const zoomTo = (value: number) => {
    if (!view || !stage) {
      return;
    }
    const cropCenter = imageToStage(
      { x: crop.x + crop.width / 2, y: crop.y + crop.height / 2 },
      view,
      stage,
    );
    zoomAround(value, {
      x: cropCenter.x - stage.width / 2,
      y: cropCenter.y - stage.height / 2,
    });
  };

  // React's onWheel is passive, so preventDefault there can't stop a
  // trackpad pinch (wheel + ctrlKey) from zooming the page. The listener
  // calls the latest handler through a ref so it's attached once.
  const wheelRef = useRef<(event: WheelEvent) => void>(() => {});
  wheelRef.current = (event: WheelEvent) => {
    const element = stageRef.current;
    if (!element || !stage) {
      return;
    }
    const rect = element.getBoundingClientRect();
    // Pinch deltas are much smaller than scroll-wheel ticks.
    const speed = event.ctrlKey ? 0.01 : 0.002;
    const deltaY = wheelDeltaPixels(
      event.deltaY,
      event.deltaMode,
      stage.height,
    );
    zoomAround(liveView.current.zoom * Math.exp(-deltaY * speed), {
      x: event.clientX - rect.left - stage.width / 2,
      y: event.clientY - rect.top - stage.height / 2,
    });
  };
  useEffect(() => {
    const element = stageRef.current;
    if (!element) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      wheelRef.current(event);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!view || event.button !== 0 || dragRef.current) {
      return;
    }
    const target = event.target as HTMLElement;
    const handle = target.closest<HTMLElement>("[data-handle]")?.dataset
      .handle as ResizeHandle | undefined;
    const origin = { x: event.clientX, y: event.clientY };
    const { pointerId } = event;
    if (handle) {
      dragRef.current = {
        mode: "resize",
        pointerId,
        origin,
        startCrop: crop,
        handle,
      };
    } else if (target.closest("[data-crop-box]")) {
      dragRef.current = { mode: "move", pointerId, origin, startCrop: crop };
    } else {
      dragRef.current = { mode: "pan", pointerId, origin, startCenter: center };
    }
    event.currentTarget.setPointerCapture(pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !view || drag.pointerId !== event.pointerId) {
      return;
    }
    const delta = {
      x: (event.clientX - drag.origin.x) / view.scale,
      y: (event.clientY - drag.origin.y) / view.scale,
    };
    if (drag.mode === "move") {
      setCrop(moveRect(drag.startCrop, delta, bounds));
    } else if (drag.mode === "resize") {
      setCrop(resizeRect(drag.startCrop, drag.handle, delta, bounds, aspect));
    } else {
      setCenter(
        clampCenter(
          {
            x: drag.startCenter.x - delta.x,
            y: drag.startCenter.y - delta.y,
          },
          bounds,
        ),
      );
    }
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const onCropKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!view) {
      return;
    }
    const step = (event.shiftKey ? 10 : 1) / view.scale;
    const deltas: Record<string, Point> = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const delta = deltas[event.key];
    if (delta) {
      event.preventDefault();
      setCrop((current) => moveRect(current, delta, bounds));
    }
  };

  const selectAspect = (value: number | null) => {
    setAspect(value);
    setCrop((current) => applyAspect(current, value, bounds));
  };

  const rotate = () => {
    const next = nextRotation(rotation);
    const nextBounds = rotatedSize(image.natural, next);
    setRotation(next);
    setCenter(rotatePointClockwise(center, bounds));
    // A 90° turn swaps the crop's width and height, which breaks any ratio
    // but 1:1, so other ratios are re-fitted instead.
    setCrop(
      aspect && !sameAspect(aspect, 1)
        ? defaultCrop(nextBounds, aspect)
        : rotateRectClockwise(crop, bounds),
    );
  };

  const reset = () => {
    setRotation(0);
    setAspect(initialAspect);
    setCrop(defaultCrop(image.natural, initialAspect));
    setZoom(MIN_ZOOM);
    setCenter({ x: image.natural.width / 2, y: image.natural.height / 2 });
  };

  const apply = async () => {
    setExporting(true);
    try {
      onApply(await renderCrop(image, rotation, crop, imageUrl));
    } catch (error) {
      console.error("[ImageCropDialog] export failed", error);
      onExportError();
    } finally {
      setExporting(false);
    }
  };

  const { translate, angle } = rotationTransform(image.natural, rotation);
  const origin =
    view && stage ? imageToStage({ x: 0, y: 0 }, view, stage) : null;
  const cropBox = view && stage ? rectToStage(crop, view, stage) : null;
  const outputPixels = outputSize(crop);
  const presets = ASPECT_PRESETS.some((preset) =>
    sameAspect(preset.value, initialAspect),
  )
    ? ASPECT_PRESETS
    : [{ label: "Original", value: initialAspect }, ...ASPECT_PRESETS];

  return (
    <>
      <div
        ref={stageRef}
        data-testid="image-crop-stage"
        className="relative min-h-0 flex-1 cursor-grab touch-none overflow-hidden bg-foreground select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {view && origin && cropBox ? (
          <>
            <img
              src={image.element.src}
              alt=""
              draggable={false}
              className="pointer-events-none absolute top-0 left-0 max-w-none origin-top-left"
              style={{
                width: image.natural.width,
                height: image.natural.height,
                transform: `translate(${origin.x}px, ${origin.y}px) scale(${view.scale}) translate(${translate.x}px, ${translate.y}px) rotate(${angle}rad)`,
              }}
            />
            {/* A focusable region so arrow keys can nudge the crop; no ARIA
                widget role fits a 2D draggable rect. */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              data-crop-box
              role="application"
              aria-roledescription="crop area"
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              aria-label="Use the arrow keys to move the crop area"
              onKeyDown={onCropKeyDown}
              className="absolute cursor-move border border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{
                left: cropBox.x,
                top: cropBox.y,
                width: cropBox.width,
                height: cropBox.height,
              }}
            >
              <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
                {Array.from({ length: 9 }, (_, i) => (
                  <div key={i} className="border-[0.5px] border-white/30" />
                ))}
              </div>
              {HANDLES.map(({ handle, className }) => (
                <div
                  key={handle}
                  data-handle={handle}
                  className={cn(
                    "absolute flex size-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center",
                    className,
                  )}
                >
                  <div className="size-3 rounded-sm border border-input bg-background shadow" />
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-t border-border px-4 py-3">
        {lockAspectRatio ? null : (
          <div
            role="group"
            aria-label="Aspect ratio"
            className="flex flex-wrap items-center gap-1"
          >
            {presets.map((preset) => (
              <Button
                key={preset.label}
                size="sm"
                variant={
                  sameAspect(preset.value, aspect) ? "secondary" : "ghost"
                }
                aria-pressed={sameAspect(preset.value, aspect)}
                onClick={() => selectAspect(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        )}
        <div className="flex min-w-[160px] flex-1 items-center gap-3">
          <Text variant="compact-description" className="text-muted-foreground">
            Zoom
          </Text>
          <Slider
            aria-label="Zoom"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={[zoom]}
            onValueChange={([value]) => zoomTo(value)}
          />
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={rotate}>
            <ArrowRotateClockwiseIcon />
            Rotate
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </div>
      </div>
      <DialogFooter className="shrink-0 items-center border-t border-border p-4 sm:justify-between">
        <Text variant="compact-description" className="text-muted-foreground">
          {outputPixels.width} × {outputPixels.height}px
        </Text>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={apply}
            loading={saving || exporting}
            disabled={saving || exporting}
          >
            Apply
          </Button>
        </div>
      </DialogFooter>
    </>
  );
};

/**
 * Crop (and rotate) an image, then hand back the result as a `File` or,
 * with `onNewImageUrl`, upload it to S3 first. Replaces the Pintura image
 * editor.
 */
const ImageCropDialog: FC<Props> = ({
  imageUrl,
  onNewImageFile,
  onNewImageUrl,
  visible,
  hide,
  aspectRatio,
  lockAspectRatio = false,
}) => {
  const [saving, setSaving] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  // A failed save belongs to that session: clear it on open, close and a
  // new image.
  useEffect(() => {
    setExportFailed(false);
  }, [visible, imageUrl]);
  const loadState = useLoadedImage(imageUrl, visible);
  const { handleFileUpload } = useS3Upload({
    onUploadComplete: (upload) => {
      setSaving(false);
      onNewImageUrl?.(upload.url);
      hide();
    },
    onUploadFailed: () => setSaving(false),
  });

  const onApply = (file: File) => {
    setExportFailed(false);
    if (onNewImageFile) {
      onNewImageFile(file);
      hide();
      return;
    }
    setSaving(true);
    handleFileUpload(file);
  };

  if (!visible) {
    return null;
  }
  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open && !saving) {
          hide();
        }
      }}
    >
      <DialogContent
        {...CONTAINED_EVENTS}
        hideDefaultTitle
        className="z-[9999] flex h-[85vh] max-h-[860px] w-[calc(100vw-32px)] max-w-4xl flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="shrink-0 border-b border-border p-4">
          <DialogTitle>Crop image</DialogTitle>
        </DialogHeader>
        {loadState.status === "ready" ? (
          <>
            {exportFailed ? (
              <Text
                variant="compact-description"
                multiline
                className="shrink-0 bg-status-error-bg px-4 py-2 text-status-error-text"
              >
                Couldn&apos;t save the cropped image. Please try again.
              </Text>
            ) : null}
            <CropEditor
              image={loadState.image}
              imageUrl={imageUrl}
              initialAspect={aspectRatio ?? null}
              lockAspectRatio={lockAspectRatio}
              saving={saving}
              onCancel={hide}
              onApply={onApply}
              onExportError={() => setExportFailed(true)}
            />
          </>
        ) : (
          <>
            <div className="flex min-h-0 flex-1 items-center justify-center bg-muted p-6">
              {loadState.status === "loading" ? (
                <Spinner size={32} />
              ) : (
                <Text
                  variant="compact-description"
                  multiline
                  className="text-center text-muted-foreground"
                >
                  This image couldn&apos;t be loaded for cropping. Check your
                  connection and try again.
                </Text>
              )}
            </div>
            <DialogFooter className="shrink-0 border-t border-border p-4">
              <Button variant="outline" onClick={hide}>
                Cancel
              </Button>
              <Button disabled>Apply</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
